<template>
    <page-controls>
        <template v-slot:left>
            <div class="grow @3xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable" data-test="segment-filters">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find Segments</span>
                        <vx-badge v-if="filtersCount > 0" :borders="false" size="xs" @click.stop="reset">
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply">
                            <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                Keyword Search
                            </filter-label>
                            <vx-input
                                type="text"
                                data-test="segment-controls-query"
                                placeholder="Search"
                                maxlength="96"
                                size="lg"
                                v-model="filters.query"
                                class="mb-4"
                            >
                                <template v-slot:right>
                                    <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                </template>
                            </vx-input>

                            <filter-label
                                :canReset="!!filters['filter[audience_type]']"
                                @reset="filters['filter[audience_type]'] = undefined"
                            >
                                Audience Type
                            </filter-label>
                            <vx-selectable
                                placeholder="Select Audience"
                                :options="audienceTypeOptions"
                                v-model="filters['filter[audience_type]']"
                                :reduce="(option) => option.value"
                                size="lg"
                                :clearable="false"
                                class="mb-4"
                            >
                            </vx-selectable>

                            <filter-label
                                :canReset="!!filters['filter[refresh_type]']"
                                @reset="filters['filter[refresh_type]'] = undefined"
                            >
                                Refresh Type
                            </filter-label>
                            <vx-selectable
                                data-test="refresh-type"
                                placeholder="Select Type"
                                :options="refreshTypeOptions"
                                v-model="filters['filter[refresh_type]']"
                                :reduce="(option) => option.value"
                                size="lg"
                                :clearable="false"
                                class="mb-4"
                            >
                            </vx-selectable>

                            <div class="mb-4">
                                <filter-label
                                    :canReset="!!filters['filter[category]']?.length"
                                    @reset="filters['filter[category]'] = undefined"
                                >
                                    Category
                                </filter-label>
                                <category-filter-select
                                    v-model="filters['filter[category]']"
                                    size="lg"
                                ></category-filter-select>
                            </div>

                            <filter-label
                                :canReset="!!filters['filter[custom_attribute_key]']?.length"
                                @reset="filters['filter[custom_attribute_key]'] = undefined"
                            >
                                Custom Attribute
                            </filter-label>
                            <vx-selectable
                                placeholder="Select Custom Attribute"
                                :options="customAttributes"
                                :loading="searchingCustomAttributes"
                                :taggable="false"
                                :clearable="false"
                                size="lg"
                                @search="searchCustomAttributes"
                                v-model="filters['filter[custom_attribute_key]']"
                                class="mb-4"
                            >
                            </vx-selectable>

                            <in-flow-filterable-item
                                v-if="FEATURES.flows && !props.orgUnitId && !props.groupId && !props.templateView"
                                class="mb-4"
                                v-model="filters['filter[flow]']"
                            >
                            </in-flow-filterable-item>

                            <div class="flex gap-2 pt-4">
                                <vx-button type="button" size="lg" color="muted-light" @click="cancel">
                                    Cancel
                                </vx-button>
                                <vx-button data-test="segment-controls-apply" size="lg" type="submit" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
                <vx-button-group size="lg" class="w-full">
                    <vx-select
                        v-model="filters.sort_field"
                        @update:modelValue="apply"
                        :options="[
                            {
                                value: 'name',
                                label: 'Sort by: Name',
                            },
                            {
                                value: 'id',
                                label: 'Sort by: Created',
                            },
                        ]"
                    ></vx-select>
                    <vx-button
                        @click.prevent="
                            filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                            apply();
                        "
                        type="button"
                        color="white"
                        shadow
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="filters.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>
        <template v-slot:right>
            <vx-button
                v-if="!props.readOnly"
                data-test="add-button"
                block
                :href="props.templateView ? '/contacts/segments/templates/new' : '/contacts/segments/new'"
                size="lg"
                color="primary"
            >
                New {{ props.templateView ? 'Template' : 'Segment' }}
                <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
            </vx-button>
        </template>
    </page-controls>
</template>

<script setup>
import { computed, ref, inject, watch, nextTick } from 'vue';
import {
    VxBadge,
    VxButton,
    VxButtonGroup,
    VxFilterable,
    VxInput,
    VxSelectable,
    VxSelect,
} from '@voxie/frontend-components';
import {
    faCircleCheck,
    faMagnifyingGlass,
    faCircleXmark,
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCirclePlus,
} from '@fortawesome/pro-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, audienceTypes } from '../../../constants/campaign';
import { SegmentTypeToLabel } from '../../../constants/segment';
import PageControls from '../../general/PageControls.vue';
import InFlowFilterableItem from '~/components/automation/flows/components/InFlowFilterableItem.vue';
import CategoryFilterSelect from '~/components/general/categories/CategoryFilterSelect.vue';
import { customAttributesClient } from '~/services';
import debounce from '~/utils/debounce';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';

const teamId = Spark.state.currentTeam.id;
const FEATURES = inject('FEATURES');

const audienceTypeOptions = computed(() =>
    audienceTypes()
        .map((audienceType) => ({
            label: audienceType.label,
            value: audienceType.name,
        }))
        .filter((audienceType) =>
            props.orgUnitId || props.groupId || props.templateView
                ? [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL].includes(audienceType.value)
                : true
        )
);

const refreshTypeOptions = computed(() =>
    Object.keys(SegmentTypeToLabel).map((refreshType) => ({
        value: refreshType,
        label: SegmentTypeToLabel[refreshType],
    }))
);

const filters = ref({});
const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    orgUnitId: Number,
    groupId: String,
    readOnly: {
        type: Boolean,
        default: false,
    },
    templateView: {
        type: Boolean,
        default: false,
    },
});
watch(
    () => props.modelValue,
    (modelValue) => {
        filters.value = { ...modelValue };
    },
    {
        deep: true,
        immediate: true,
    }
);

const emit = defineEmits(['update:modelValue', 'apply', 'reset']);

const active = ref(false);

const toggleFilterable = (event) => {
    if (!customAttributes.value.length && event) {
        searchCustomAttributes();
    }

    active.value = event;
};

const isFlowFilterApplied = computed(() => {
    if (!props.modelValue['filter[flow]']) {
        return false;
    }

    return props.modelValue['filter[flow]'].in_flow !== undefined || !!props.modelValue['filter[flow]'].flow_id;
});

const filtersCount = computed(
    () =>
        (props.modelValue['filter[audience_type]'] ? 1 : 0) +
        (props.modelValue['filter[refresh_type]'] ? 1 : 0) +
        (props.modelValue.query ? 1 : 0) +
        (isFlowFilterApplied.value ? 1 : 0) +
        (props.modelValue['filter[category]']?.length ? 1 : 0) +
        (props.modelValue['filter[custom_attribute_key]']?.length ? 1 : 0)
);

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...props.modelValue };
    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};

const customAttributes = ref([]);
const searchingCustomAttributes = ref(false);
const searchCustomAttributes = debounce((search) => {
    searchingCustomAttributes.value = true;
    customAttributesClient
        .customAttributesAutocomplete(teamId, {
            query: search,
        })
        .then((response) => {
            customAttributes.value = response.data.data;
            searchingCustomAttributes.value = false;
        });
}, 200);
</script>
